/**
 * The leaderboard — shown ONLY at the end of a round and at game end
 * (SPEC §2.4). Never per turn; the reveal screen deliberately has no
 * leaderboard on it.
 *
 * It is the deck's ranking row with points on the right, and rank 1 carries the
 * same filled terracotta the reveal gives the two ends of an order.
 */
import { motion } from 'motion/react';
import type { Leaderboard, PlayerId } from '@idgame/protocol';
import { LicenseCard, type LicensePlayer } from './LicenseCard.js';

export interface LeaderboardListProps {
  leaderboard: Leaderboard;
  players: ReadonlyMap<PlayerId, LicensePlayer>;
  selfId?: PlayerId;
  /** Round points are meaningless on a final board that spans every round. */
  showRoundPoints?: boolean;
  /** A single quiet line on final results; never interrupts live play. */
  showScoringHint?: boolean;
}

export function LeaderboardList({
  leaderboard,
  players,
  selfId,
  showRoundPoints = true,
  showScoringHint = false,
}: LeaderboardListProps) {
  const rankCounts = new Map<number, number>();
  for (const row of leaderboard.rows) {
    rankCounts.set(row.rank, (rankCounts.get(row.rank) ?? 0) + 1);
  }

  return (
    <>
      <ol className="space-y-1.5" data-testid="leaderboard">
        {leaderboard.rows.map((row, index) => {
          const player = players.get(row.playerId);
          if (!player) return null;
          const isSelf = row.playerId === selfId;
          const leading = row.rank === 1;
          const tied = (rankCounts.get(row.rank) ?? 0) > 1;
          return (
            <motion.li
              key={row.playerId}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.06, duration: 0.3 }}
              data-testid={`leaderboard-row-${row.playerId}`}
            >
              <LicenseCard
                player={{ ...player, isSelf }}
                variant="chip"
                rank={row.rank}
                tone={leading ? 'top' : 'neutral'}
              >
                <span className="shrink-0 text-right">
                  <span className="block text-[0.9375rem] font-semibold tabular-nums">
                    {tied ? 'Tied · ' : ''}
                    {row.total}
                  </span>
                  {showRoundPoints && (
                    // Secondary type has to switch with the fill: `ink-soft` is
                    // invisible on terracotta, and the row already carries cream.
                    <span
                      className={`block text-[0.6875rem] tabular-nums ${
                        leading ? 'text-cream/75' : 'text-ink-soft'
                      }`}
                    >
                      {row.roundPoints > 0 ? `+${row.roundPoints}` : '—'} this round
                    </span>
                  )}
                </span>
              </LicenseCard>
            </motion.li>
          );
        })}
      </ol>
      {showScoringHint ? (
        <p className="mt-3 text-center text-[0.6875rem] leading-snug text-ink-soft">
          Correct first and last picks earn points. Get both right for a 1.5× bonus.
        </p>
      ) : null}
    </>
  );
}
